import { createApi } from "@reduxjs/toolkit/query/react";

import baseQuery from "redux/baseQuery";
import { removeCreadential, setCredential } from "redux/slices/user";

import axios from "utils/axios";

import { User, UserCredential } from "./types";

export const apiService = createApi({
  reducerPath: 'apiService',
  baseQuery: baseQuery,
  tagTypes: [
    'datalake',
    'menu',
    'user',
    'role',
    'permission',
    'cctv',
    'config',
    'datasets',
    'dashboard',
    'warehouseConfig'
  ],
  endpoints: build => ({
    getMe: build.query<User, void>({
      query: () => ({ url: '/users/me' }),
      providesTags: () => [{ type: 'user', id: 'me' }]
    }),
    login: build.mutation<any, UserCredential>({
      queryFn: async ({ email, password }) => {
        try {
          const result = await axios.post("/login", {
            email,
            password
          })
          return { data: result.data }
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: {
                message: error.response?.data?.message || error.message
              }
            }
          }
        }
      },
      invalidatesTags: () => [{ type: 'user', id: 'me' }],
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled
          const { data: loggedInUser } = await dispatch(
            apiService.endpoints.getMe.initiate()
          )
          dispatch(setCredential(loggedInUser))
        } catch (error) {
          console.log(error.error?.data?.message)
        }
      }
    }),
    logout: build.mutation<any, void>({
      queryFn: async () => {
        try {
          const result = await axios.post("/logout")
          return { data: result.data }
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: {
                message: error.response?.data?.message || error.message
              }
            }
          }
        }
      },
      invalidatesTags: () => [{ type: 'user', id: 'me' }],
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        await queryFulfilled
        dispatch(removeCreadential())
      }
    }),
    forgotPassword: build.mutation<{ status: string }, { email: string }>({
      query: ({ email }) => {
        return {
          url: "/forgot-password",
          method: 'post',
          data: { email }
        }
      }
    }),
    resetPassword: build.mutation<
      { message: string },
      {
        email: string
        token: string
        password: string
        password_confirmation: string
      }
    >({
      query: ({ email, token, password, password_confirmation }) => {
        return {
          url: "/reset-password",
          method: 'post',
          data: { email, token, password, password_confirmation }
        }
      }
    }),
    changePassword: build.mutation<
      any,
      {
        current_password: string
        password: string
        password_confirmation: string
      }
    >({
      query: ({ current_password, password, password_confirmation }) => ({
        url: "/user/password",
        method: 'PUT',
        data: { current_password, password, password_confirmation }
      })
    })
  })
})

export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useLogoutMutation,
  useGetMeQuery
} = apiService
